import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  Typography,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import PaymentService from '../services/payment.service';
import { NumberTextField } from './common';
import { requestService } from '../services/requestService';
import api from '../api/axios';

interface AddPaymentModalProps {
  open: boolean;
  onClose: () => void;
  request: any;
  onSuccess: () => void;
}

const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  open,
  onClose,
  request,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string>('');

  const [formData, setFormData] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    amount: 0,
    paymentMethod: 'bankTransfer' as 'cash' | 'card' | 'bankTransfer' | 'check' | 'other',
    reference: '',
    notes: '',
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setReceiptFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setReceiptPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview('');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.amount || formData.amount <= 0) {
      setError('Please enter a valid payment amount.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const attrs = request?.attributes || request;
      const invoice = attrs.invoice?.data || attrs.invoice;

      if (!invoice?.id) {
        setError('No invoice found for this request.');
        return;
      }

      // Upload receipt file if provided
      let uploadedFileId = null;
      if (receiptFile) {
        const fileFormData = new FormData();
        fileFormData.append('files', receiptFile);

        try {
          const uploadResponse = await api.post('/upload', fileFormData);

          if (uploadResponse.data && uploadResponse.data.length > 0) {
            uploadedFileId = uploadResponse.data[0].id;
          }
        } catch (uploadError: any) {
          console.error('Error uploading file:', uploadError);
          const errMsg = uploadError?.response?.data?.error?.message || 'Failed to upload payment receipt. Please try again.';
          setError(errMsg);
          setLoading(false);
          return;
        }
      }

      // Create payment (backend assigns PAY-YYYY-MM-####)
      const paymentData: any = {
        paymentNumber: '',
        invoice: invoice.id,
        paymentDate: formData.paymentDate,
        amount: formData.amount,
        paymentMethod: formData.paymentMethod,
        reference: formData.reference,
        notes: formData.notes,
      };

      // Add receipt if uploaded
      if (uploadedFileId) {
        paymentData.paymentReceipt = uploadedFileId;
      }

      await PaymentService.create(paymentData);

      // Update request status to payment_received
      await requestService.update(request.id, {
        status: 'payment_received',
        paymentReceivedAt: new Date().toISOString(),
      });

      setSuccess('Payment recorded successfully!');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Error creating payment:', err);
      setError(err.response?.data?.error?.message || 'Failed to record payment.');
    } finally {
      setLoading(false);
    }
  };

  const attrs = request?.attributes || request;
  const invoice = attrs?.invoice?.data?.attributes || attrs?.invoice;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Record Payment for Request #{attrs?.requestNumber || request?.id}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Invoice Info */}
            {invoice && (
              <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Invoice Information
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {invoice?.invoiceNumber || 'N/A'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total: OMR {invoice?.total?.toFixed(3) || '0.000'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Balance Due: OMR {invoice?.balanceDue?.toFixed(3) || '0.000'}
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label="Payment Date"
              type="date"
              value={formData.paymentDate}
              onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
            />

            <NumberTextField
              fullWidth
              label="Amount"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
              InputProps={{
                startAdornment: <InputAdornment position="start">OMR</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={formData.paymentMethod}
                label="Payment Method"
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value as any })}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="bankTransfer">Bank Transfer</MenuItem>
                <MenuItem value="check">Check</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Reference Number"
              value={formData.reference}
              onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              placeholder="Transaction ID, Check number, etc."
            />

            {/* File Upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Payment Receipt (Optional)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ mb: 1 }}
              >
                {receiptFile ? receiptFile.name : 'Upload Receipt'}
                <input
                  type="file"
                  hidden
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
              </Button>
              
              {receiptFile && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                  <AttachFileIcon fontSize="small" color="primary" />
                  <Typography variant="body2" color="textSecondary">
                    {receiptFile.name} ({(receiptFile.size / 1024).toFixed(2)} KB)
                  </Typography>
                </Stack>
              )}

              {receiptPreview && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <img
                    src={receiptPreview}
                    alt="Receipt preview"
                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
                  />
                </Box>
              )}
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional information about this payment"
            />
          </Stack>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Recording...' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPaymentModal;

